import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import type { Scrollbar as ReactScrollbar } from 'react-scrollbars-custom';
import { ScrollState } from 'react-scrollbars-custom/dist/types/types';

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ko } from 'date-fns/locale';

import { Scrollbar } from '../Scrollbar';
import {
  calendarDaysGrid,
  calendarGradientOverlay,
  calendarMonthContainer,
  calendarMonthHeader,
  calendarScrollWrapper,
  calendarWeekRow,
  rangeDatePickerCalendarHeight,
  singleDatePickerCalendarHeight,
} from './DatePicker.css';
import { DatePickerDay } from './DatePickerDay';

interface BaseDatePickerCalendarProps {
  minDate?: Date;
  maxDate?: Date;
  initialMonth?: Date;
}

interface SingleDatePickerCalendarProps extends BaseDatePickerCalendarProps {
  mode: 'single';
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
}

interface RangeDatePickerCalendarProps extends BaseDatePickerCalendarProps {
  mode: 'range';
  selected?: DateRange;
  onSelect?: (range: DateRange | undefined) => void;
}

type DatePickerCalendarProps = SingleDatePickerCalendarProps | RangeDatePickerCalendarProps;

const INITIAL_MONTHS_COUNT = 6;
const LOAD_MORE_THRESHOLD = 100;

export interface DatePickerCalendarRef {
  getCurrentViewportMonth: () => Date | null;
  resetMonths: (baseMonth: Date) => void;
  scrollToMonth: (targetMonth: Date) => boolean;
}

export const DatePickerCalendar = forwardRef<DatePickerCalendarRef, DatePickerCalendarProps>(
  (props, ref) => {
    const { mode, selected, minDate, maxDate, initialMonth } = props;
    const scrollbarRef = useRef<ReactScrollbar | null>(null);
    const [months, setMonths] = useState<Date[]>(() => {
      const baseMonth = initialMonth || new Date();
      const result: Date[] = [];
      for (let i = -2; i < INITIAL_MONTHS_COUNT - 2; i++) {
        result.push(addMonths(startOfMonth(baseMonth), i));
      }
      return result;
    });
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
    const [isLoadingTop, setIsLoadingTop] = useState(false);
    const [isLoadingBottom, setIsLoadingBottom] = useState(false);

    // Range selection state
    const rangeFrom = mode === 'range' && selected ? selected.from : undefined;
    const rangeTo = mode === 'range' && selected ? selected.to : undefined;

    // Generate weeks for a month
    const getWeeksInMonth = useCallback((month: Date) => {
      const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start, end });

      const weeks: Date[][] = [];
      for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
      }
      return weeks;
    }, []);

    // Check if date is disabled
    const isDateDisabled = useCallback(
      (date: Date) => {
        if (minDate && isBefore(date, minDate)) return true;
        if (maxDate && isAfter(date, maxDate)) return true;
        return false;
      },
      [minDate, maxDate],
    );

    // Check if date is in range
    const isDateInRange = useCallback(
      (date: Date) => {
        if (mode !== 'range') return false;
        if (!rangeFrom) return false;

        const endDate = rangeTo || hoveredDate;
        if (!endDate) return false;

        const [start, end] = isBefore(rangeFrom, endDate)
          ? [rangeFrom, endDate]
          : [endDate, rangeFrom];

        return (
          isWithinInterval(date, { start, end }) && !isSameDay(date, start) && !isSameDay(date, end)
        );
      },
      [mode, rangeFrom, rangeTo, hoveredDate],
    );

    // Check if date is range start
    const isRangeStart = useCallback(
      (date: Date) => {
        if (mode !== 'range' || !rangeFrom) return false;
        const endDate = rangeTo || hoveredDate;
        if (!endDate) return isSameDay(date, rangeFrom);
        return isSameDay(date, isBefore(rangeFrom, endDate) ? rangeFrom : endDate);
      },
      [mode, rangeFrom, rangeTo, hoveredDate],
    );

    // Check if date is range end
    const isRangeEnd = useCallback(
      (date: Date) => {
        if (mode !== 'range' || !rangeFrom) return false;
        const endDate = rangeTo || hoveredDate;
        if (!endDate) return false;
        return isSameDay(date, isBefore(rangeFrom, endDate) ? endDate : rangeFrom);
      },
      [mode, rangeFrom, rangeTo, hoveredDate],
    );

    // Handle date click
    const handleDateClick = useCallback(
      (date: Date) => {
        if (props.mode === 'single') {
          props.onSelect?.(date);
        } else {
          // Range mode
          if (!rangeFrom || (rangeFrom && rangeTo)) {
            // Start new selection
            props.onSelect?.({ from: date, to: undefined });
          } else {
            // Complete selection
            if (isBefore(date, rangeFrom)) {
              props.onSelect?.({ from: date, to: rangeFrom });
            } else {
              props.onSelect?.({ from: rangeFrom, to: date });
            }
          }
        }
      },
      [props, rangeFrom, rangeTo],
    );

    // Handle mouse enter for range preview
    const handleMouseEnter = useCallback(
      (date: Date) => {
        if (mode === 'range' && rangeFrom && !rangeTo) {
          setHoveredDate(date);
        }
      },
      [mode, rangeFrom, rangeTo],
    );

    const handleMouseLeave = useCallback(() => {
      setHoveredDate(null);
    }, []);

    // Load more months
    const loadMoreMonths = useCallback(
      (direction: 'top' | 'bottom') => {
        if (direction === 'top' && !isLoadingTop) {
          setIsLoadingTop(true);
          const firstMonth = months[0];
          const newMonths: Date[] = [];
          for (let i = 3; i > 0; i--) {
            newMonths.push(subMonths(firstMonth, i));
          }
          setMonths((prev) => [...newMonths, ...prev]);
          setIsLoadingTop(false);
        } else if (direction === 'bottom' && !isLoadingBottom) {
          setIsLoadingBottom(true);
          const lastMonth = months[months.length - 1];
          const newMonths: Date[] = [];
          for (let i = 1; i <= 3; i++) {
            newMonths.push(addMonths(lastMonth, i));
          }
          setMonths((prev) => [...prev, ...newMonths]);
          setIsLoadingBottom(false);
        }
      },
      [months, isLoadingTop, isLoadingBottom],
    );

    // Handle scroll
    const handleScroll = useCallback(
      (scrollValues: ScrollState) => {
        const { scrollTop, scrollHeight, clientHeight } = scrollValues;

        // Load more at top
        if (scrollTop < LOAD_MORE_THRESHOLD) {
          const scrollElement = scrollbarRef.current?.contentElement;
          const previousScrollHeight = scrollElement?.scrollHeight ?? scrollHeight;
          loadMoreMonths('top');
          // Maintain scroll position after adding months at top
          requestAnimationFrame(() => {
            const updatedScrollElement = scrollbarRef.current?.contentElement;
            const scrollbarInstance = scrollbarRef.current;
            if (updatedScrollElement && scrollbarInstance) {
              const newScrollHeight = updatedScrollElement.scrollHeight;
              scrollbarInstance.scrollTop = scrollTop + (newScrollHeight - previousScrollHeight);
            }
          });
        }

        // Load more at bottom
        if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD) {
          loadMoreMonths('bottom');
        }
      },
      [loadMoreMonths],
    );

    // Get current viewport month
    const getCurrentViewportMonth = useCallback((): Date | null => {
      const scrollElement = scrollbarRef.current?.contentElement;
      const scrollbarInstance = scrollbarRef.current;
      if (!scrollElement || !scrollbarInstance) return null;

      const scrollTop = scrollbarInstance.scrollTop;
      const viewportHeight = scrollElement.clientHeight;
      const viewportTop = scrollTop;
      const viewportBottom = scrollTop + viewportHeight;

      const monthElements = scrollElement.querySelectorAll('[data-month]');
      Array.from(monthElements).forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        const elementTop = element.offsetTop;
        const elementBottom = elementTop + element.offsetHeight;

        // Check if element is in viewport (at least partially visible)
        if (elementBottom > viewportTop && elementTop < viewportBottom) {
          const monthStr = element.dataset.month;
          if (monthStr) {
            const [year, month] = monthStr.split('-').map(Number);
            return new Date(year, month - 1, 1);
          }
        }
      });

      return null;
    }, []);

    // Reset months array
    const resetMonths = useCallback((baseMonth: Date) => {
      const result: Date[] = [];
      for (let i = -2; i < INITIAL_MONTHS_COUNT - 2; i++) {
        result.push(addMonths(startOfMonth(baseMonth), i));
      }
      setMonths(result);
    }, []);

    const scrollToMonthIndex = useCallback((targetMonth: Date) => {
      const scrollElement = scrollbarRef.current?.contentElement;
      const scrollbarInstance = scrollbarRef.current;
      if (!scrollElement || !scrollbarInstance) return false;

      const monthElements = scrollElement.querySelectorAll('[data-month]');
      const targetElement = Array.from(monthElements).find(
        (element) => (element as HTMLElement).dataset.month === format(targetMonth, 'yyyy-MM'),
      );
      if (!targetElement || !(targetElement instanceof HTMLElement)) return false;

      scrollbarInstance.scrollTop = targetElement.offsetTop - 20;
      return true;
    }, []);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getCurrentViewportMonth,
        resetMonths,
        scrollToMonth: scrollToMonthIndex,
      }),
      [getCurrentViewportMonth, resetMonths, scrollToMonthIndex],
    );

    // Scroll to initial month on mount
    useEffect(() => {
      const scrollToInitialMonth = () => {
        const targetMonth = initialMonth || new Date();
        return scrollToMonthIndex(targetMonth);
      };

      // DOM이 렌더링될 때까지 대기
      const attemptScroll = () => {
        if (!scrollToInitialMonth()) {
          // 요소가 아직 준비되지 않았으면 다음 프레임에서 재시도
          requestAnimationFrame(() => {
            if (!scrollToInitialMonth()) {
              // 한 번 더 재시도
              setTimeout(() => {
                scrollToInitialMonth();
              }, 0);
            }
          });
        }
      };

      // 초기 스크롤 시도
      requestAnimationFrame(attemptScroll);
    }, [initialMonth, scrollToMonthIndex]);

    return (
      <div className={calendarScrollWrapper}>
        <Scrollbar
          ref={scrollbarRef}
          noScrollX
          style={{
            height:
              mode === 'range'
                ? `${rangeDatePickerCalendarHeight}px`
                : `${singleDatePickerCalendarHeight}px`,
          }}
          scrollbarStyle={{ paddingLeft: 12, paddingRight: 0 }}
          scrollerProps={{ tabIndex: -1 }}
          onScroll={handleScroll}
        >
          {months.map((month) => {
            const weeks = getWeeksInMonth(month);
            return (
              <div
                key={format(month, 'yyyy-MM')}
                data-month={format(month, 'yyyy-MM')}
                className={calendarMonthContainer}
              >
                <div className={calendarMonthHeader}>
                  {format(month, 'yyyy년 M월', { locale: ko })}
                </div>
                <div className={calendarDaysGrid}>
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className={calendarWeekRow}>
                      {week.map((day, dayIndex) => {
                        const isOutside = !isSameMonth(day, month);
                        const isSelected =
                          mode === 'single' && selected instanceof Date && isSameDay(day, selected);
                        const isStartOfWeek = dayIndex === 0; // 월요일 (주의 시작)
                        const isEndOfWeek = dayIndex === week.length - 1; // 일요일 (주의 끝)

                        return (
                          <DatePickerDay
                            key={day.toISOString()}
                            date={day}
                            isSelected={isSelected}
                            isToday={isToday(day)}
                            isRangeStart={isRangeStart(day)}
                            isRangeEnd={isRangeEnd(day)}
                            isInRange={isDateInRange(day)}
                            isOutsideMonth={isOutside}
                            isDisabled={isDateDisabled(day)}
                            isStartOfWeek={isStartOfWeek}
                            isEndOfWeek={isEndOfWeek}
                            onClick={handleDateClick}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </Scrollbar>
        <div className={calendarGradientOverlay} />
      </div>
    );
  },
);

DatePickerCalendar.displayName = 'DatePickerCalendar';
